import React from 'react';
import { Check, Tv, Upload, Search, Radio } from 'lucide-react';
import type { PublisherConfig } from '../../types/selfService';

interface CampaignStatusDashboardProps {
  config: PublisherConfig;
  currentStep?: number;
  compact?: boolean;
  mini?: boolean;
  // CTV-578: once the campaign has finished delivering, relabel the final
  // step from the in-progress "Delivering" to the past-tense "Delivered".
  completed?: boolean;
}

const STEPS = [
  { icon: Upload, title: 'Commercial submitted', shortTitle: 'Submitted', description: 'Your ad has been received' },
  { icon: Search, title: 'Commercial under review', shortTitle: 'Reviewed', description: 'The publisher is reviewing your ad' },
  { icon: Tv, title: 'Ad approved', shortTitle: 'Approved', description: 'Cleared to run' },
  { icon: Radio, title: 'Delivering', shortTitle: 'Delivering', description: 'Your campaign is on air' },
];

const CampaignStatusDashboard: React.FC<CampaignStatusDashboardProps> = ({
  config,
  currentStep = 1,
  compact = false,
  mini = false,
  completed = false,
}) => {
  const accent = config.accentColor || config.primaryColor;

  const steps = completed
    ? STEPS.map((step, index) =>
        index === STEPS.length - 1
          ? { ...step, title: 'Delivered', shortTitle: 'Delivered', description: 'Your campaign has finished' }
          : step,
      )
    : STEPS;

  // Mini renders an inline single-line breadcrumb of small dots + tiny labels.
  // Used on active campaign cards where the progress bar is the focus.
  if (mini) {
    return (
      <div className="flex items-center justify-between gap-1.5 px-0.5">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          return (
            <React.Fragment key={step.title}>
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="w-3 h-3 rounded-full flex items-center justify-center shrink-0 transition-all"
                  style={{
                    backgroundColor: isCompleted ? accent : isCurrent ? 'white' : 'rgb(226, 232, 240)',
                    border: isCurrent ? `1.5px solid ${accent}` : 'none',
                    boxShadow: isCurrent ? `0 0 0 2px ${accent}22` : 'none',
                  }}
                >
                  {isCompleted && <Check className="w-2 h-2 text-white" strokeWidth={3.5} />}
                </span>
                <span
                  className={`text-[10px] font-medium uppercase tracking-[0.08em] truncate ${
                    isCompleted || isCurrent ? '' : 'text-slate-400'
                  }`}
                  style={{ color: isCompleted || isCurrent ? accent : undefined }}
                >
                  {step.shortTitle}
                </span>
              </div>
              {index < steps.length - 1 && (
                <span
                  className="flex-1 h-px"
                  style={{
                    background: index < currentStep - 1
                      ? accent
                      : `linear-gradient(90deg, ${accent}40, rgba(226, 232, 240, 0.6))`,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Each icon centers at (i + 0.5) / steps.length, so the line should start
  // at the first icon's center and end at the last's. Margins = 0.5/N * 100%.
  const sideInset = (0.5 / steps.length) * 100;
  const lineRange = 100 - sideInset * 2;
  const filledStep = Math.min(currentStep, steps.length - 1);

  return (
    <div className="relative">
      {/* Background line */}
      <div
        className={`absolute ${compact ? 'top-4' : 'top-6'} left-0 right-0 h-0.5 bg-slate-200`}
        style={{ marginLeft: `${sideInset}%`, marginRight: `${sideInset}%` }}
      />
      {/* Progress line */}
      <div
        className={`absolute ${compact ? 'top-4' : 'top-6'} h-0.5 transition-all duration-500`}
        style={{
          marginLeft: `${sideInset}%`,
          width: `${(filledStep / (steps.length - 1)) * lineRange}%`,
          backgroundColor: accent,
        }}
      />

      <div className="relative flex items-start justify-between gap-1">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const Icon = step.icon;

          return (
            <div key={step.title} className="flex-1 flex flex-col items-center text-center">
              <div
                className={`rounded-full flex items-center justify-center border-2 transition-all z-10 ${
                  compact
                    ? (isCurrent ? 'w-10 h-10 -mt-1 shadow' : 'w-8 h-8')
                    : (isCurrent ? 'w-16 h-16 -mt-2 shadow-lg' : 'w-12 h-12')
                } ${
                  isCompleted
                    ? 'text-white'
                    : isCurrent
                    ? 'bg-white'
                    : 'bg-slate-50 border-slate-200 text-slate-400'
                }`}
                style={
                  isCompleted
                    ? { backgroundColor: accent, borderColor: accent }
                    : isCurrent
                    ? { borderColor: accent, color: accent, boxShadow: `0 0 0 4px ${accent}22, 0 4px 10px ${accent}33` }
                    : undefined
                }
              >
                {isCompleted ? (
                  <Check className={compact ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
                ) : (
                  <Icon className={
                    compact
                      ? (isCurrent ? 'w-4 h-4 animate-pulse' : 'w-3.5 h-3.5')
                      : (isCurrent ? 'w-7 h-7 animate-pulse' : 'w-5 h-5')
                  } />
                )}
              </div>
              <div className={compact ? 'mt-1.5 px-1' : 'mt-3 px-1'}>
                <div
                  className={`leading-tight ${
                    compact
                      ? (isCurrent ? 'text-[11px] font-semibold' : 'text-[11px] font-medium')
                      : (isCurrent ? 'text-sm md:text-base font-semibold' : 'text-xs md:text-sm font-medium')
                  } ${
                    isCompleted || isCurrent ? 'text-slate-900' : 'text-slate-500'
                  }`}
                  style={isCurrent ? { color: accent } : undefined}
                >
                  {step.title}
                </div>
                {!compact && (
                  <div
                    className={`text-[11px] mt-1 leading-tight ${
                      isCurrent ? 'text-slate-700 font-medium' : 'hidden sm:block text-slate-500'
                    }`}
                  >
                    {step.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignStatusDashboard;
